import { ArrowLeftOutlined } from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Card, Col, Flex, Row, theme, Typography,
} from 'antd';
import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate, useParams,
} from 'react-router-dom';

import {
    projectsMutations, projectsQueries,
} from '@/api';
import {
    ConfirmModal, getActionControlStyleVars, ProjectForm, ProjectFormRef,
} from '@/components';
import { AppRoute } from '@/constants';
import { useUIStore } from '@/stores';

export const ProjectEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const formRef = useRef<ProjectFormRef>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [formData, setFormData] = useState<{
        title: string;
        description?: string | null;
        comment?: string | null;
        regulation?: string | null;
        fields?: Array<{ name: string; value: string }> | null;
    } | null>(null);

    const { setPageTitle } = useUIStore();

    const { data: project, isLoading, refetch } = useQuery({
        ...projectsQueries.getOneProject(id || ''),
        refetchOnWindowFocus: false,
    });
    const updateMutationOptions = projectsMutations.update(id || '');
    const { mutate: updateProject, isPending } = useMutation(updateMutationOptions);

    const handleSubmit = useCallback((data: {
        title: string;
        description?: string | null;
        comment?: string | null;
        regulation?: string | null;
        fields?: Array<{ name: string; value: string }> | null;
    }) => {
        setFormData(data);
        setConfirmModalOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData) {
            updateProject(formData, {
                onSuccess: async () => {
                    setConfirmModalOpen(false);
                    setFormData(null);
                    await refetch();
                },
            });
        }
    }, [formData, updateProject, refetch]);

    useEffect(() => {
        setPageTitle(project?.title ?? null);
        return () => setPageTitle(null);
    }, [project?.title, setPageTitle]);

    const handleCancel = useCallback(() => {
        navigate(`${AppRoute.Projects}/${id}`);
    }, [navigate, id]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    return (
        <>
            <Helmet>
                <title>Редагувати проект</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={18} xl={16}>
                    <Card
                        title={
                            <Flex align="center" gap="middle">
                                <Link
                                    to={AppRoute.Projects}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Редагувати проект
                                </Typography.Title>
                            </Flex>
                        }
                    >
                        <ProjectForm
                            ref={formRef}
                            initData={project || null}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isLoading || isPending}
                            cancelTo={`${AppRoute.Projects}/${id}`}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="update"
                entityType="project"
                entityName={formData?.title || project?.title}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />
        </>
    );
};
